/**
 * ForceGraphCanvas — Single Source of Truth for Graph Rendering
 * 
 * This component handles all graph visualization using react-force-graph-2d.
 * It's designed to be reusable across:
 * - GraphExplorer page (full exploration interface)
 * - SubgraphModal (chat transcript widget)
 * - Any future graph visualization needs
 * 
 * What this component handles:
 * - Force-directed graph rendering
 * - Node styling (colors, sizes, labels, glow effects)
 * - Edge styling (colors, widths, predicates)
 * - Interaction (hover, click, zoom, pan)
 * - Zoom controls overlay
 * 
 * What this component does NOT handle:
 * - Data fetching (caller provides graphData)
 * - Filtering UI (caller provides filtered data)
 * - Entity/document pickers (specific to GraphExplorer)
 */

import React, { useState, useCallback, useRef, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Box, IconButton, ButtonGroup, Tooltip, Typography, Chip, Paper, Fade, useMediaQuery, useTheme } from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

import type { GraphNode, ForceGraphCanvasProps } from './graph-types';
import {
    NODE_COLORS,
    getHighlightColors,
    getGraphBg,
    getLabelColors,
    NODE_SIZING,
    getEdgeStyles,
    getGlowEffects,
    FORCE_CONFIG,
    ZOOM_CONFIG,
    getControlStyles,
    LABEL_STYLES,
} from './graph-theme';
import { useCustomThemeContext } from '../../lib/contexts/ThemeContext';

import './ForceGraphCanvas.css';

/**
 * Helper to get node ID whether the link source/target is a string or object
 */
function getLinkNodeId(node: string | GraphNode): string {
    if (typeof node === 'string') return node;
    if (typeof node === 'object' && node !== null) return node.id;
    return '';
}

/**
 * ForceGraphCanvas Component
 */
const ForceGraphCanvas = forwardRef<any, ForceGraphCanvasProps>(({
    graphData,
    width = '100%',
    height = '100%',
    backgroundColor,
    showControls = true,
    showLegend = false,
    onNodeClick,
    compact = false,
    viewMode = 'searched',
    retrievedIds = [],
    usedIds = [],
}, ref) => {
    const { isDarkMode } = useCustomThemeContext();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const highlightColors = useMemo(() => getHighlightColors(isDarkMode), [isDarkMode]);
    const labelColors = useMemo(() => getLabelColors(isDarkMode), [isDarkMode]);
    const edgeStyles = useMemo(() => getEdgeStyles(isDarkMode), [isDarkMode]);
    const glowEffects = useMemo(() => getGlowEffects(isDarkMode), [isDarkMode]);
    const controlStyles = useMemo(() => getControlStyles(isDarkMode), [isDarkMode]);
    const themeBg = useMemo(() => getGraphBg(isDarkMode), [isDarkMode]);

    // Internal refs
    const graphRef = useRef<any>();
    const containerRef = useRef<HTMLDivElement>(null);

    // State
    const [hoveredNode, setHoveredNode] = useState<GraphNode | null>(null);
    const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
    const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

    // Expose graph ref to parent
    useImperativeHandle(ref, () => graphRef.current);

    // Compute dimensions from container
    useEffect(() => {
        if (!containerRef.current) return;

        const updateDimensions = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                setDimensions({
                    width: rect.width || 800,
                    height: rect.height || 600,
                });
            }
        };

        updateDimensions();

        const observer = new ResizeObserver(updateDimensions);
        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, []);

    // Process graph data: add colors and compute degrees
    const processedData = useMemo(() => {
        const { nodes, links } = graphData;
    
        // Compute degree for each node
        const degreeMap = new Map<string, number>();
        links.forEach(link => {
            const sourceId = getLinkNodeId(link.source);
            const targetId = getLinkNodeId(link.target);
            degreeMap.set(sourceId, (degreeMap.get(sourceId) || 0) + 1);
            degreeMap.set(targetId, (degreeMap.get(targetId) || 0) + 1);
        });
    
        const maxDegree = Math.max(...Array.from(degreeMap.values()), 1);
    
        // Add computed properties to nodes, filtering out unconnected Entities
        const processedNodes = nodes
            .filter(node => node.type !== 'Entity' || (degreeMap.get(node.id) || 0) > 0)
            .map(node => ({
                ...node,
                color: NODE_COLORS[node.type] || '#999',
                degree: degreeMap.get(node.id) || 0,
                normalizedDegree: (degreeMap.get(node.id) || 0) / maxDegree,
            }));
    
        return { nodes: processedNodes, links };
    }, [graphData]);

    // Compute connected node IDs for highlighting
    const connectedNodeIds = useMemo(() => {
        if (!selectedNode) return new Set<string>();

        const connected = new Set<string>([selectedNode.id]);
        processedData.links.forEach(link => {
            const sourceId = getLinkNodeId(link.source);
            const targetId = getLinkNodeId(link.target);
            if (sourceId === selectedNode.id) connected.add(targetId);
            if (targetId === selectedNode.id) connected.add(sourceId);
        });
        return connected;
    }, [selectedNode, processedData.links]);

    // Configure forces after data loads
    useEffect(() => {
        if (graphRef.current && processedData.nodes.length > 0) {
            const fg = graphRef.current;
            fg.d3Force('charge')?.strength(FORCE_CONFIG.chargeStrength);
            fg.d3Force('link')?.distance(FORCE_CONFIG.linkDistance);

            // Add a weak centering force (gravity) to all nodes
            // This prevents isolated nodes from drifting too far away from the main cluster
            fg.d3Force('gravity', (alpha: number) => {
                const strength = (FORCE_CONFIG as any).gravity || 0.01;
                processedData.nodes.forEach((node: any) => {
                    node.vx -= node.x * strength * alpha;
                    node.vy -= node.y * strength * alpha;
                });
            });
        }
    }, [processedData.nodes]);

    // Zoom to fit when data changes
    useEffect(() => {
        if (graphRef.current && processedData.nodes.length > 0) {
            // Initial zoom to fit after a short delay
            setTimeout(() => {
                graphRef.current?.zoomToFit(ZOOM_CONFIG.zoomToFitDuration, ZOOM_CONFIG.zoomToFitPadding);
            }, 500);

            // Second pass zoom to fit after more settling
            setTimeout(() => {
                graphRef.current?.zoomToFit(ZOOM_CONFIG.zoomToFitDuration, ZOOM_CONFIG.zoomToFitPadding);
            }, 2000);
        }
    }, [processedData.nodes.length]);

    // Node click handler
    const handleNodeClick = useCallback((node: any) => {
        const newSelected = node?.id === selectedNode?.id ? null : node;
        setSelectedNode(newSelected);
        onNodeClick?.(newSelected);
    }, [selectedNode, onNodeClick]);

    // Node canvas rendering
    const nodeCanvasObject = useCallback((node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
        const label = node.label || '';
        const isEntity = node.type === 'Entity';
        const isHovered = hoveredNode?.id === node.id;
        const isSelected = selectedNode?.id === node.id;
        const isConnectedToSelected = selectedNode && connectedNodeIds.has(node.id);

        // View mode logic
        const isRetrieved = retrievedIds.includes(node.id);
        const isUsed = usedIds.includes(node.id);

        let isMuted = false;
        let isHighlightedByMode = false;

        if (viewMode === 'retrieved') {
            if (isRetrieved) isHighlightedByMode = true;
            else isMuted = true;
        } else if (viewMode === 'used') {
            if (isUsed) isHighlightedByMode = true;
            else isMuted = true;
        }

        // Apply muting/highlighting
        ctx.save();
        ctx.globalAlpha = isMuted ? 0.2 : 1.0;

        // Calculate radius
        const baseRadius = NODE_SIZING.baseRadius[node.type as keyof typeof NODE_SIZING.baseRadius] || 5;
        const degreeBonus = isEntity ? Math.sqrt(node.degree || 0) * NODE_SIZING.degreeMultiplier : 0;
        const highlightBonus = isSelected ? NODE_SIZING.selectedBonus : isConnectedToSelected ? NODE_SIZING.connectedBonus : 0;
        const modeBonus = isHighlightedByMode ? 6 : 0;
        const radius = baseRadius + degreeBonus + (isHovered ? NODE_SIZING.hoverBonus : 0) + highlightBonus + modeBonus;

        // Draw glow for selected/connected/mode-highlighted nodes
        if (isSelected || isConnectedToSelected || isHighlightedByMode) {
            ctx.save();
            const glow = isSelected
                ? glowEffects.selected
                : isHighlightedByMode
                    ? { color: node.color, blur: 25 }
                    : glowEffects.connected;
            ctx.shadowColor = glow.color;
            ctx.shadowBlur = glow.blur;
            ctx.beginPath();
            ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
            ctx.fillStyle = node.color;
            ctx.fill();
            ctx.restore();
        } else {
            ctx.beginPath();
            ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI, false);
            ctx.fillStyle = node.color;
            ctx.fill();
        }

        // Draw border
        ctx.strokeStyle = isSelected
            ? highlightColors.selected
            : isHighlightedByMode
                ? node.color
                : isConnectedToSelected
                    ? highlightColors.connected
                    : isHovered
                        ? labelColors.default
                        : isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)';
        ctx.lineWidth = isSelected || isHighlightedByMode ? 4 : isConnectedToSelected ? 3 : isHovered ? 2 : 1;
        ctx.stroke();

        // Draw label (show for selected, connected, hovered, zoomed in, or high-degree)
        const showLabel = isSelected || isConnectedToSelected || isHighlightedByMode || globalScale > 2 || isHovered || (isEntity && node.normalizedDegree > 0.5);

        if (showLabel && !compact) {
            const fontSize = Math.min(Math.max(LABEL_STYLES.fontSize.node / globalScale, LABEL_STYLES.fontSize.nodeMin), LABEL_STYLES.fontSize.nodeMax);
            ctx.font = `${isSelected || isHovered ? 'bold ' : ''}${fontSize}px ${LABEL_STYLES.fontFamily}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const maxLen = isSelected || isHovered ? 50 : 20;
            const truncatedLabel = label.length > maxLen ? label.substring(0, maxLen) + '…' : label;
            const textY = node.y + radius + fontSize / 2 + 4;

            // Background for selected/connected labels
            if (isSelected || isConnectedToSelected) {
                const textWidth = ctx.measureText(truncatedLabel).width;
                ctx.fillStyle = isDarkMode ? 'rgba(30, 40, 60, 0.95)' : 'rgba(255,255,255,0.95)';
                ctx.fillRect(node.x - textWidth / 2 - 4, textY - fontSize / 2 - 2, textWidth + 8, fontSize + 4);
            }

            ctx.fillStyle = isSelected
                ? highlightColors.selected
                : isHighlightedByMode
                    ? node.color
                    : isConnectedToSelected
                        ? highlightColors.connected
                        : isHovered
                            ? labelColors.default
                            : labelColors.secondary;
            ctx.fillText(truncatedLabel, node.x, textY);
        }
        ctx.restore();
    }, [hoveredNode, selectedNode, connectedNodeIds, compact, viewMode, retrievedIds, usedIds, highlightColors, labelColors, glowEffects, isDarkMode]);

    // Link canvas rendering (for edge labels)
    const linkCanvasObject = useCallback((link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
        const start = link.source;
        const end = link.target;
        if (typeof start !== 'object' || typeof end !== 'object') return;

        const sourceId = start.id;
        const targetId = end.id;

        // Muting logic for links
        let isMuted = false;
        if (viewMode === 'retrieved') {
            if (!retrievedIds.includes(sourceId) && !retrievedIds.includes(targetId)) isMuted = true;
        } else if (viewMode === 'used') {
            if (!usedIds.includes(sourceId) && !usedIds.includes(targetId)) isMuted = true;
        }

        ctx.save();
        ctx.globalAlpha = isMuted ? 0.1 : 1.0;

        const isConnectedToSelected = selectedNode && (sourceId === selectedNode.id || targetId === selectedNode.id);

        // Show edge labels at lower zoom (0.8x) or when connected to selected
        const label = link.label || link.predicate || (link.type === 'FACT' ? null : null);
        if (label && (globalScale > 0.8 || isConnectedToSelected)) {
            const midX = (start.x + end.x) / 2;
            const midY = (start.y + end.y) / 2;
            const fontSize = Math.min(Math.max(LABEL_STYLES.fontSize.edge / globalScale, LABEL_STYLES.fontSize.edgeMin), LABEL_STYLES.fontSize.edgeMax);

            ctx.font = `italic ${isConnectedToSelected ? 'bold ' : ''}${fontSize}px ${LABEL_STYLES.fontFamily}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            const textWidth = ctx.measureText(label).width;
            const padding = 4;
            const bgHeight = fontSize + padding;
            const bgWidth = textWidth + padding * 2;

            // Draw pill background
            ctx.fillStyle = isConnectedToSelected ? edgeStyles.labelBg.highlighted : edgeStyles.labelBg.default;
            ctx.beginPath();
            ctx.roundRect(midX - bgWidth / 2, midY - bgHeight / 2, bgWidth, bgHeight, 8);
            ctx.fill();

            // Draw border
            ctx.strokeStyle = isConnectedToSelected ? edgeStyles.labelBorder.highlighted : edgeStyles.labelBorder.default;
            ctx.lineWidth = isConnectedToSelected ? 1.5 : 0.5;
            ctx.stroke();

            // Draw text
            ctx.fillStyle = isConnectedToSelected ? highlightColors.edge : labelColors.secondary;
            ctx.fillText(label, midX, midY);
        }
        ctx.restore();
    }, [selectedNode, viewMode, retrievedIds, usedIds, highlightColors, labelColors, edgeStyles]);

    // Link color
    const linkColor = useCallback((link: any) => {
        const sourceId = getLinkNodeId(link.source);
        const targetId = getLinkNodeId(link.target);

        // Muting logic for links
        let isMuted = false;
        if (viewMode === 'retrieved') {
            if (!retrievedIds.includes(sourceId) && !retrievedIds.includes(targetId)) isMuted = true;
        } else if (viewMode === 'used') {
            if (!usedIds.includes(sourceId) && !usedIds.includes(targetId)) isMuted = true;
        }

        const isConnected = selectedNode && (sourceId === selectedNode.id || targetId === selectedNode.id);
        if (isConnected) return highlightColors.edge;

        const baseColor = link.type === 'FACT' ? edgeStyles.colors.fact : edgeStyles.colors.default;
        if (isMuted) return baseColor.replace(/[\d.]+\)$/, '0.05)'); // Very transparent
        return baseColor;
    }, [selectedNode, viewMode, retrievedIds, usedIds, highlightColors, edgeStyles]);

    // Link width
    const linkWidth = useCallback((link: any) => {
        const sourceId = getLinkNodeId(link.source);
        const targetId = getLinkNodeId(link.target);

        // Muting logic for links
        let isMuted = false;
        if (viewMode === 'retrieved') {
            if (!retrievedIds.includes(sourceId) && !retrievedIds.includes(targetId)) isMuted = true;
        } else if (viewMode === 'used') {
            if (!usedIds.includes(sourceId) && !usedIds.includes(targetId)) isMuted = true;
        }

        const isConnected = selectedNode && (sourceId === selectedNode.id || targetId === selectedNode.id);
        if (isConnected) return edgeStyles.width.highlighted;

        const baseWidth = link.type === 'FACT' ? edgeStyles.width.fact : edgeStyles.width.default;
        return isMuted ? baseWidth * 0.5 : baseWidth;
    }, [selectedNode, viewMode, retrievedIds, usedIds, edgeStyles]);

    // Arrow color
    const arrowColor = useCallback((link: any) => {
        const sourceId = getLinkNodeId(link.source);
        const targetId = getLinkNodeId(link.target);
        const isConnected = selectedNode && (sourceId === selectedNode.id || targetId === selectedNode.id);
        if (isConnected) return highlightColors.edge;
        return link.type === 'FACT' ? NODE_COLORS.Entity : labelColors.muted;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNode, highlightColors, labelColors]);

    const bg = backgroundColor || themeBg;

    // Get unique node types present in the data
    const nodeTypes = useMemo(() => {
        const types = new Set(processedData.nodes.map(n => n.type));
        return Array.from(types);
    }, [processedData.nodes]);

    // Determine which chunk to show text for (hover takes precedence over selection)
    const activeChunk = useMemo(() => {
        if (hoveredNode?.type === 'Chunk' && hoveredNode.text) {
            return hoveredNode;
        }
        if (selectedNode?.type === 'Chunk' && selectedNode.text) {
            return selectedNode;
        }
        return null;
    }, [hoveredNode, selectedNode]);

    return (
        <div
            ref={containerRef}
            className="force-graph-canvas-container"
            style={{ width, height, position: 'relative', backgroundColor: bg }}
        >
            {/* Zoom Controls */}
            {showControls && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 16,
                        left: 16,
                        zIndex: 20,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                    }}
                >
                    <ButtonGroup orientation="vertical" variant="contained" size="small">
                        <Tooltip title="Zoom In" placement="right">
                            <IconButton
                                onClick={() => graphRef.current?.zoom(graphRef.current.zoom() * ZOOM_CONFIG.zoomStep, 300)}
                                sx={{
                                    bgcolor: controlStyles.backgroundColor,
                                    color: labelColors.default,
                                    '&:hover': { bgcolor: controlStyles.hoverBackgroundColor }
                                }}
                            >
                                <ZoomInIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Zoom Out" placement="right">
                            <IconButton
                                onClick={() => graphRef.current?.zoom(graphRef.current.zoom() / ZOOM_CONFIG.zoomStep, 300)}
                                sx={{
                                    bgcolor: controlStyles.backgroundColor,
                                    color: labelColors.default,
                                    '&:hover': { bgcolor: controlStyles.hoverBackgroundColor }
                                }}
                            >
                                <ZoomOutIcon />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Fit to Screen" placement="right">
                            <IconButton
                                onClick={() => graphRef.current?.zoomToFit(ZOOM_CONFIG.zoomToFitDuration, ZOOM_CONFIG.zoomToFitPadding)}
                                sx={{
                                    bgcolor: controlStyles.backgroundColor,
                                    color: labelColors.default,
                                    '&:hover': { bgcolor: controlStyles.hoverBackgroundColor }
                                }}
                            >
                                <CenterFocusStrongIcon />
                            </IconButton>
                        </Tooltip>
                    </ButtonGroup>
                    {!compact && (
                        <Typography variant="caption" sx={{ color: labelColors.muted, mt: 1, fontSize: '10px' }}>
                            Scroll to zoom<br />Drag to pan
                        </Typography>
                    )}
                </Box>
            )}

            {/* Legend */}
            {showLegend && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 16,
                        left: 16,
                        zIndex: 20,
                    }}
                >
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {nodeTypes.map(type => (
                            <Chip
                                key={type}
                                size="small"
                                label={type}
                                sx={{ bgcolor: NODE_COLORS[type], color: 'white', fontSize: '10px' }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* Entity hover tooltip */}
            {hoveredNode && hoveredNode.type === 'Entity' && !compact && (
                <Fade in={true} timeout={200}>
                    <Paper
                        elevation={4}
                        sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            zIndex: 20,
                            p: 2,
                            maxWidth: 280,
                            bgcolor: isDarkMode ? 'background.paper' : 'white',
                            border: '2px solid',
                            borderColor: NODE_COLORS.Entity,
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: NODE_COLORS.Entity }}>
                            {hoveredNode.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {hoveredNode.degree || 0} connections
                        </Typography>
                    </Paper>
                </Fade>
            )}

            {/* Chunk text viewer */}
            {activeChunk && !compact && (
                <Fade in={true} timeout={200}>
                    <Paper
                        elevation={6}
                        sx={(theme) => ({
                            position: 'absolute',
                            bottom: 16,
                            right: 16,
                            zIndex: 20,
                            p: 2,
                            width: 400,
                            maxHeight: 300,
                            overflow: 'auto',
                            bgcolor: isDarkMode ? 'background.paper' : 'white',
                            border: '2px solid',
                            borderColor: NODE_COLORS.Chunk,
                            borderRadius: 2,
                            [theme.breakpoints.down('sm')]: {
                                left: 16,
                                right: 16,
                                width: 'auto',
                                maxHeight: '40dvh',
                            },
                        })}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: NODE_COLORS.Chunk }}>
                                📄 Chunk Text {activeChunk.id === selectedNode?.id ? '(Selected)' : '(Hovered)'}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    if (isMobile) {
                                        setHoveredNode(null);
                                        setSelectedNode(null);
                                        onNodeClick?.(null);
                                        return;
                                    }
                                    if (activeChunk.id === selectedNode?.id) setSelectedNode(null);
                                }}
                                sx={{ color: labelColors.default }}
                            >
                                ✕
                            </IconButton>
                        </Box>
                        <Typography
                            variant="body2"
                            sx={{
                                whiteSpace: 'pre-wrap',
                                fontFamily: 'monospace',
                                fontSize: '12px',
                                lineHeight: 1.5,
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
                                color: labelColors.default,
                                p: 1.5,
                                borderRadius: 1,
                            }}
                        >
                            {activeChunk.text}
                        </Typography>
                    </Paper>
                </Fade>
            )}

            {/* Empty state */}
            {processedData.nodes.length === 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: labelColors.muted,
                    }}
                >
                    <Typography>No graph data to display</Typography>
                </Box>
            ) : (
                <ForceGraph2D
                    ref={graphRef}
                    graphData={processedData}
                    width={dimensions.width}
                    height={dimensions.height}
                    backgroundColor={bg}
                    nodeCanvasObject={nodeCanvasObject}
                    nodePointerAreaPaint={(node: any, color, ctx) => {
                        const radius = 15 + Math.sqrt(node.degree || 0) * NODE_SIZING.degreeMultiplier;
                        ctx.fillStyle = color;
                        ctx.beginPath();
                        ctx.arc(node.x, node.y, radius, 0, 2 * Math.PI);
                        ctx.fill();
                    }}
                    linkColor={linkColor}
                    linkWidth={linkWidth}
                    linkCurvature={edgeStyles.curvature}
                    linkDirectionalArrowLength={edgeStyles.arrow.length}
                    linkDirectionalArrowRelPos={edgeStyles.arrow.relPos}
                    linkDirectionalArrowColor={arrowColor}
                    linkCanvasObjectMode={() => 'after'}
                    linkCanvasObject={linkCanvasObject}
                    d3AlphaDecay={FORCE_CONFIG.alphaDecay}
                    d3VelocityDecay={FORCE_CONFIG.velocityDecay}
                    enableNodeDrag={true}
                    enableZoomInteraction={true}
                    enablePanInteraction={true}
                    minZoom={ZOOM_CONFIG.min}
                    maxZoom={ZOOM_CONFIG.max}
                    onEngineStop={() => graphRef.current?.zoomToFit(ZOOM_CONFIG.zoomToFitDuration, ZOOM_CONFIG.zoomToFitPadding)}
                    onNodeHover={setHoveredNode}
                    onNodeClick={handleNodeClick}
                    onBackgroundClick={() => {
                        setSelectedNode(null);
                        onNodeClick?.(null);
                    }}
                />
            )}
        </div>
    );
});

ForceGraphCanvas.displayName = 'ForceGraphCanvas';

export default ForceGraphCanvas;
